import { useEffect, useState, useCallback } from 'react';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Loader2, Shield, Ban, TestTube, User as UserIcon } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';
import { OverviewPanel } from './panels/OverviewPanel';
import { CreditsPanel } from './panels/CreditsPanel';
import { SubscriptionPanel } from './panels/SubscriptionPanel';
import { ActivityPanel } from './panels/ActivityPanel';
import { ErrorsPanel } from './panels/ErrorsPanel';
import { ReviewPanel } from './panels/ReviewPanel';
import { SettingsPanel } from './panels/SettingsPanel';
import { DangerPanel } from './panels/DangerPanel';
import type { UserDetail } from './types';

interface Props {
  userId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUserChanged: () => void;
}

export function UserDetailDrawer({ userId, open, onOpenChange, onUserChanged }: Props) {
  const [detail, setDetail] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('admin-get-user-detail', {
        body: { target_user_id: userId },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      setDetail(data as UserDetail);
    } catch (err) {
      toast({
        title: 'Failed to load user',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [userId, toast]);

  useEffect(() => {
    if (open && userId) {
      setDetail(null);
      load();
    }
  }, [open, userId, load]);

  const handleChange = useCallback(() => {
    load();
    onUserChanged();
  }, [load, onUserChanged]);

  const initials = (detail?.user.email ?? '??')
    .split('@')[0]
    .split(/[._-]/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const errorCount = detail
    ? detail.stats.error_reports_count + detail.stats.graph_errors_count
    : 0;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-3xl overflow-y-auto p-0">
        {loading || !detail ? (
          <div className="flex items-center justify-center py-32">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <>
            {/* Hero header */}
            <SheetHeader className="space-y-3 p-6 border-b border-border/60 bg-gradient-to-br from-primary/5 to-background">
              <div className="flex items-start gap-4">
                <div
                  className={cn(
                    'h-12 w-12 rounded-xl text-primary-foreground flex items-center justify-center font-semibold shrink-0 shadow-fluent-2',
                    detail.is_banned
                      ? 'bg-gradient-to-br from-destructive to-destructive/80'
                      : 'bg-gradient-to-br from-primary to-primary-glow'
                  )}
                >
                  {initials || <UserIcon className="h-5 w-5" />}
                </div>
                <div className="min-w-0 flex-1">
                  <SheetTitle className="text-lg truncate text-left">
                    {detail.user.email}
                  </SheetTitle>
                  <SheetDescription className="text-left">
                    Full account view: activity, credits, subscription, and admin controls.
                  </SheetDescription>
                </div>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap">
                {detail.is_admin && (
                  <Badge className="gap-1 bg-primary/15 text-primary border border-primary/30 hover:bg-primary/20">
                    <Shield className="h-3 w-3" /> Admin
                  </Badge>
                )}
                {detail.is_banned && (
                  <Badge variant="destructive" className="gap-1">
                    <Ban className="h-3 w-3" /> Banned
                  </Badge>
                )}
                {detail.settings?.use_test_stripe && (
                  <Badge
                    variant="outline"
                    className="gap-1 border-warning/40 text-warning bg-warning/5"
                  >
                    <TestTube className="h-3 w-3" /> Test Stripe
                  </Badge>
                )}
                {detail.subscription?.tier && (
                  <Badge variant="outline" className="capitalize">
                    {detail.subscription.tier} plan
                  </Badge>
                )}
                {!detail.is_admin && !detail.is_banned && !detail.settings?.use_test_stripe && (
                  <Badge
                    variant="outline"
                    className="border-success/30 text-success bg-success/5"
                  >
                    Active user
                  </Badge>
                )}
              </div>
            </SheetHeader>

            {/* Tabs */}
            <div className="p-6">
              <Tabs defaultValue="overview" className="space-y-4">
                <div className="rounded-xl border border-border/60 bg-card p-1.5 -mx-1">
                  <TabsList className="flex flex-wrap h-auto gap-1 bg-transparent p-0 w-full justify-start">
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="credits">Credits</TabsTrigger>
                    <TabsTrigger value="subscription">Subscription</TabsTrigger>
                    <TabsTrigger value="activity">Activity</TabsTrigger>
                    <TabsTrigger value="errors">
                      Errors{errorCount > 0 ? ` (${errorCount})` : ''}
                    </TabsTrigger>
                    <TabsTrigger value="reviews">
                      Reviews
                      {detail.review ? (detail.review.is_featured ? ' ⭐' : ' •') : ''}
                    </TabsTrigger>
                    <TabsTrigger value="settings">Settings & Roles</TabsTrigger>
                    <TabsTrigger
                      value="danger"
                      className="text-destructive data-[state=active]:text-destructive"
                    >
                      Danger
                    </TabsTrigger>
                  </TabsList>
                </div>

                <TabsContent value="overview" className="mt-0">
                  <OverviewPanel detail={detail} />
                </TabsContent>
                <TabsContent value="credits" className="mt-0">
                  <CreditsPanel detail={detail} onChange={handleChange} />
                </TabsContent>
                <TabsContent value="subscription" className="mt-0">
                  <SubscriptionPanel detail={detail} />
                </TabsContent>
                <TabsContent value="activity" className="mt-0">
                  <ActivityPanel detail={detail} />
                </TabsContent>
                <TabsContent value="errors" className="mt-0">
                  <ErrorsPanel detail={detail} />
                </TabsContent>
                <TabsContent value="reviews" className="mt-0">
                  <ReviewPanel detail={detail} onChange={handleChange} />
                </TabsContent>
                <TabsContent value="settings" className="mt-0">
                  <SettingsPanel detail={detail} onChange={handleChange} />
                </TabsContent>
                <TabsContent value="danger" className="mt-0">
                  <DangerPanel detail={detail} />
                </TabsContent>
              </Tabs>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
